// @ts-strict-ignore
import React, { useMemo } from 'react'
import { startCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem } from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { InternalTransferTransactionSubType } from 'pages/WalletDetails/components/RecentActivity/SidePanel/PaymentTemplates/InternalTransfer'
import InternalTransferSenderReceiver from 'pages/WalletDetails/components/RecentActivity/SidePanel/PaymentTemplates/InternalTransferSenderReceiver'
import { TransactionContent } from 'types/transaction'
import { TransactionType } from 'types/transaction/TransactionType'
import { paymentDirections } from 'types/types'
import { useTransactionDetailsTransformer } from '../hooks/useTransactionDetailsTransformer'
import { DataListItemCustom } from './DataListCustom'

const shortenLongText = {
  START: 15,
  END: 10,
}

interface SidePanelBodyProps {
  transactionDetails: TransactionContent
}

export const SidePanelBody = ({ transactionDetails }: SidePanelBodyProps) => {
  const { t } = useTranslation()
  const isBvnkNetworkTransfer = transactionDetails?.type === TransactionType.BVNK_NETWORK_TRANSFER
  const isInternalTransfer = transactionDetails?.type === TransactionType.INTERNAL_TRANSFER
  const isBuySell = transactionDetails?.type === 'buy' || transactionDetails?.type === 'sell'

  const {
    amountWithCurrency,
    transactionType,
    customerReference,
    transactionHash,
    transactionDate,
    customTextCopies,
    hasDirectionOrIsChannelsDeposit,
    walletAddresses,
    walletName,
    transactionUUID,
    transactionReference,
    withdrawalDetails,
    showSentFromWalletAddress,
    showSentToWalletAddress,
    transformedData,
    exchangeRate,
    fromCurrency,
    toCurrency,
    transactionSubType,
    name,
  } = useTransactionDetailsTransformer(transactionDetails, t)
  const transactionTypeSubType = `${transactionType}_${transactionSubType}`
  const isUserDeposit = transactionType === TransactionType.USER_DEPOSIT

  const summaryTextCopies = {
    IN: {
      walletAddress: t('wallets.searchTransaction.payInWalletAddress'),
      amount: t('wallets.searchTransaction.payInAmount'),
      paidWallet: t('wallets.searchTransaction.paidInWallet'),
    },
    OUT: {
      walletAddress: t('wallets.searchTransaction.payOutWalletAddress'),
      amount: t('wallets.searchTransaction.payOutAmount'),
      paidWallet: t('wallets.searchTransaction.paidOutWallet'),
    },
  }

  const primaryAmountLabel = useMemo(() => {
    if (isBvnkNetworkTransfer) {
      switch (transactionSubType) {
        case InternalTransferTransactionSubType.PAYMENT_IN:
          return t('amountRecieved')
        case InternalTransferTransactionSubType.PAYMENT_OUT:
          return t('transactions.sideBar.amountWithdrawn')
        default:
          return t('amount')
      }
    } else {
      return transformedData?.resolvedPaymentDirection == paymentDirections.IN
        ? customTextCopies?.[transactionType]?.amountReceived
        : customTextCopies?.[transactionType]?.amountPaid
    }
  }, [
    customTextCopies,
    isBvnkNetworkTransfer,
    transformedData?.resolvedPaymentDirection,
    transactionSubType,
  ])

  const secondaryAmountLabel =
    transformedData?.resolvedPaymentDirection == paymentDirections.IN
      ? customTextCopies?.[transactionType]?.amountPaid
      : customTextCopies?.[transactionType]?.amountReceived

  const isFdusdTradeInSettlement = transactionType === TransactionType.FDUSD_TRADE_IN_SETTLEMENT

  return (
    <Box paddingB={16}>
      <h4 className="text-foreground-prominent text-h4">{t('transactionDetails')}</h4>
      <Box paddingT={8} paddingB={8}>
        {!isBuySell && amountWithCurrency && (
          <DataListItem
            id="amount-received"
            testId="amount-received"
            title={primaryAmountLabel || t('amountRecieved')}
            body={amountWithCurrency}
          />
        )}
        {!isBuySell && transformedData?.fromAmount && hasDirectionOrIsChannelsDeposit && (
          <DataListItem
            id="amount-paid"
            testId="amount-paid"
            title={secondaryAmountLabel || t('wallets.searchTransaction.paymentInAmountPaid')}
            body={transformedData?.fromAmount}
          />
        )}
        {isBuySell && !!transformedData?.fromAmount && (
          <DataListItem
            id="from-amount"
            testId="from-amount"
            title={t('transactions.sideBar.fromAmount')}
            body={transformedData?.fromAmount}
          />
        )}
        {isBuySell && !!transactionDetails?.toAmount && (
          <DataListItem
            id="amount-received"
            testId="amount-received"
            title={t('transactions.sideBar.amountReceived')}
            body={`${transactionDetails?.toAmount} ${toCurrency}`}
          />
        )}
        {isBuySell && !!exchangeRate && (
          <DataListItem
            id="exchange-rate"
            testId="exchange-rate"
            title={t('wallets.searchTransaction.exchangeRate')}
            body={`1 ${fromCurrency} = ${1 / exchangeRate} ${toCurrency}`}
          />
        )}
        {!!transactionType && (
          <DataListItem
            id="transaction-type"
            testId="transaction-type"
            title={t('transactionType')}
            body={startCase(transactionType)}
          />
        )}
        {transactionType == 'paymentOut' && (
          <DataListItem
            id="exchange-rate"
            testId="exchange-rate"
            title={t('wallets.searchTransaction.exchangeRate')}
            body={`1 ${fromCurrency} = ${exchangeRate} ${toCurrency}`}
          />
        )}
        {transactionType == 'paymentIn' && (
          <DataListItem
            id="exchange-rate"
            testId="exchange-rate"
            title={t('wallets.searchTransaction.exchangeRate')}
            body={`1 ${fromCurrency} = ${exchangeRate} ${toCurrency}`}
          />
        )}
        {transactionType === TransactionType.LATE_PAYMENT && (
          <DataListItem
            id="exchange-rate"
            testId="exchange-rate"
            title={t('wallets.searchTransaction.exchangeRate')}
            body={`1 ${fromCurrency} = ${exchangeRate} ${toCurrency}`}
          />
        )}
        {walletName && (hasDirectionOrIsChannelsDeposit || isUserDeposit) && (
          <DataListItem
            id="paid-wallet"
            testId="paid-wallet"
            title={
              summaryTextCopies?.[
                transactionDetails?.direction ?? transformedData?.resolvedPaymentDirection
              ]?.paidWallet
            }
            body={startCase(walletName)}
          />
        )}
        {walletName && transactionType === TransactionType.USER_WITHDRAWAL && (
          <DataListItem
            id="sent-from-wallet"
            testId="sent-from-wallet"
            title={t('wallets.searchTransaction.sentFromWallet')}
            body={startCase(walletName)}
          />
        )}
        {name && isFdusdTradeInSettlement && (
          <DataListItem
            id="received-from"
            testId="received-from"
            title={t('wallets.searchTransaction.receivedFrom')}
            body={startCase(name)}
          />
        )}
        {showSentFromWalletAddress && (
          <DataListItemCustom
            id="pay-in-wallet-address"
            testId="pay-in-wallet-address"
            title={t('wallets.searchTransaction.payInWalletAddress')}
            textList={walletAddresses}
          />
        )}
        {showSentToWalletAddress &&
          transactionDetails?.type !== TransactionType.USER_WITHDRAWAL && (
            <DataListItemCustom
              id="pay-out-wallet-address"
              testId="pay-out-wallet-address"
              title={t('wallets.searchTransaction.payOutWalletAddress')}
              textList={walletAddresses}
            />
          )}
        {!!walletAddresses?.length && hasDirectionOrIsChannelsDeposit && (
          <DataListItemCustom
            id="summary-text-copies"
            testId="summary-text-copies"
            title={
              summaryTextCopies?.[
                transactionDetails?.direction ?? transformedData?.resolvedPaymentDirection
              ]?.walletAddress
            }
            textList={walletAddresses}
          />
        )}
        {!!customerReference && (
          <DataListItem
            id="customer-reference"
            testId="customer-reference"
            title={t('customerReference')}
            body={ellipseMiddleText(customerReference, shortenLongText.START, shortenLongText.END)}
            copyText={customerReference}
          />
        )}
        {!!transactionUUID && (
          <DataListItem
            id="transaction-uuid"
            testId="transaction-uuid"
            title={t('transactionUuid')}
            body={ellipseMiddleText(transactionUUID, shortenLongText.START, shortenLongText.END)}
            copyText={transactionUUID}
          />
        )}
        {isBuySell && !!transactionReference && (
          <DataListItem
            id="transaction-reference"
            testId="transaction-reference"
            title={t('transactionReference')}
            body={ellipseMiddleText(
              transactionReference,
              shortenLongText.START,
              shortenLongText.END
            )}
            copyText={transactionReference}
          />
        )}
        {!!transactionHash && (
          <DataListItem
            id="transaction-hash"
            testId="transaction-hash"
            title={t('hash')}
            body={ellipseMiddleText(transactionHash, shortenLongText.START, shortenLongText.END)}
            copyText={transactionHash}
          />
        )}
        {!!transactionDate && (
          <DataListItem
            id="transaction-date"
            testId="transaction-date"
            title={t('date')}
            body={transactionDate}
          />
        )}
      </Box>
      {!!withdrawalDetails && (
        <>
          <h4 className="text-h4 text-foreground-prominent mt-3">
            {transactionType === TransactionType.USER_DEPOSIT && t('senderDetails')}
            {transactionType === TransactionType.USER_WITHDRAWAL && t('withdrawalDetails')}
          </h4>
          <Box gapY={8} paddingB={8}>
            {!!transactionDetails?.accountNumber && (
              <DataListItem
                id="account-number"
                testId="account-number"
                title={t('accountNumber')}
                body={transactionDetails?.accountNumber}
              />
            )}
            {!!transactionDetails?.bankName && (
              <DataListItem
                id="bank-name"
                testId="bank-name"
                title={t('bankName')}
                body={transactionDetails?.name}
              />
            )}
            {!!transactionDetails?.bankCode && (
              <DataListItem
                id="bank-code"
                testId="bank-code"
                title={t('bankCode')}
                body={transactionDetails?.bankCode}
              />
            )}
            {!!transactionDetails?.name && transactionType !== TransactionType.USER_DEPOSIT && (
              <DataListItem
                id="beneficiary-name"
                testId="beneficiary-name"
                title={t('beneficiaryName')}
                body={transactionDetails?.name}
              />
            )}
          </Box>
        </>
      )}
      {(isInternalTransfer || isBvnkNetworkTransfer) && (
        <InternalTransferSenderReceiver
          transactionTypeSubType={transactionTypeSubType}
          sender={transactionDetails?.name}
          beneficiary={transactionDetails?.name}
        />
      )}
    </Box>
  )
}
